import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Stack
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { fetchFilters, createFilter, updateFilter, deleteFilter, fileToBase64 } from '../api/filtersApi';
import { useTranslation } from 'react-i18next';

const Filters = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [currentFilter, setCurrentFilter] = useState({ name: '', icon: null });
  const [isEdit, setIsEdit] = useState(false);
  const [iconPreview, setIconPreview] = useState(null);
  const [iconFile, setIconFile] = useState(null);

  useEffect(() => {
    loadFilters();
  }, []);

  const loadFilters = async () => {
    setLoading(true);
    try {
      const data = await fetchFilters();
      setFilters(data || []);
    } catch (error) {
      Swal.fire(t('Error'), t('Failed to load filters'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (filter = null) => {
    if (filter) {
      setCurrentFilter({ 
        Id: filter.Id, 
        name: filter.name,
        icon: filter.Icon 
      });
      setIconPreview(filter.IconUrl || null);
      setIsEdit(true);
    } else {
      setCurrentFilter({ name: '', icon: null });
      setIconPreview(null);
      setIsEdit(false);
    }
    setIconFile(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIconPreview(null);
    setIconFile(null);
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        Swal.fire(t('Error'), t('Please select an image file'), 'error');
        return;
      }
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        Swal.fire(t('Error'), t('Image size should not exceed 2MB'), 'error');
        return;
      }
      setIconFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setIconPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveIcon = () => {
    setIconFile(null);
    setIconPreview(null);
    setCurrentFilter({ ...currentFilter, icon: null });
  };

  const handleSave = async () => {
    if (!currentFilter.name) {
      Swal.fire(t('Error'), t('Name is required'), 'error');
      return;
    }

    try {
      let iconData = currentFilter.icon;

      // Convert new icon file to base64 if uploaded
      if (iconFile) {
        iconData = await fileToBase64(iconFile);
      } else if (iconPreview === null && isEdit) {
        // Icon was removed
        iconData = null;
      }

      const payload = {
        name: currentFilter.name,
        icon: iconData
      };

      if (isEdit) {
        await updateFilter(currentFilter.Id, payload);
        Swal.fire(t('Success'), t('Filter updated successfully'), 'success');
      } else {
        await createFilter(payload);
        Swal.fire(t('Success'), t('Filter created successfully'), 'success');
      }
      
      loadFilters();
      setOpen(false);
    } catch (error) {
      console.error('Save error:', error);
      Swal.fire(t('Error'), t('Failed to save filter'), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    const result = await Swal.fire({
      title: t('Delete'),
      text: t('Are you sure you want to delete this filter?'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('Yes'),
      cancelButtonText: t('No')
    });
    if (result.isConfirmed) {
      try {
        await deleteFilter(id);
        Swal.fire(t('Success'), t('Filter deleted successfully'), 'success');
        loadFilters();
      } catch (error) {
        Swal.fire(t('Error'), t('Failed to delete filter'), 'error');
      }
    }
  };

  return (
    <Box p={2}>
      <Card>
        <CardHeader
          avatar={<FilterListIcon />}
          title={t('Filters')}
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
              {t('Add')}
            </Button>
          }
        />
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('ID')}</TableCell>
                  <TableCell>{t('Icon')}</TableCell>
                  <TableCell>{t('Name')}</TableCell>
                  <TableCell align="right">{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filters.map((filter) => (
                  <TableRow key={filter.Id}>
                    <TableCell>{filter.Id}</TableCell>
                    <TableCell>
                      {filter.IconUrl ? (
                        <Avatar 
                          src={filter.IconUrl} 
                          alt={filter.name}
                          variant="rounded"
                          sx={{ width: 40, height: 40 }}
                        />
                      ) : (
                        <Avatar 
                          variant="rounded"
                          sx={{ width: 40, height: 40, bgcolor: 'grey.300' }}
                        >
                          <ImageIcon />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>{filter.name}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpen(filter)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(filter.Id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filters.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('No filters found')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEdit ? t('Edit Filter') : t('Add Filter')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label={t('Name')}
              fullWidth
              value={currentFilter.name}
              onChange={e => setCurrentFilter({ ...currentFilter, name: e.target.value })}
              required
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('Icon')}
              </Typography>
              
              {iconPreview ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                    src={iconPreview}
                    variant="rounded"
                    sx={{ width: 100, height: 100, border: '2px solid #e0e0e0' }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' }
                    }}
                    onClick={handleRemoveIcon}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<ImageIcon />}
                  sx={{ mt: 1 }}
                >
                  {t('Upload Icon')}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleIconChange}
                  />
                </Button>
              )}
              
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                {t('Recommended: Square image, max 2MB')}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('Cancel')}</Button>
          <Button onClick={handleSave} variant="contained">
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Filters;
